import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SBack } from '@/components/ui/molecules/SBack';
import { ThemeToggle } from '@/components/ui/atoms/ThemeToggle';
import Stoggle from '@/components/ui/atoms/Stoggle';
import { useNotificationSettings, useUpdateNotificationSettings } from '@/hooks/useNotifications';
import type { NotificationChannels } from '@/services/notification.service';
import './NotificationPreference.css';

const PUSH_CHANNELS: {
  key: keyof NotificationChannels;
  label: string;
  description: string;
}[] = [
  {
    key: 'promotions',
    label: 'Promotions',
    description: 'Offers, discounts and member perks',
  },
  {
    key: 'announcements',
    label: 'Location Announcements',
    description: 'News and updates from your Tee Box',
  },
  {
    key: 'accountAlerts',
    label: 'Account Alerts',
    description: 'Bookings, billing and account activity',
  },
];

const NotificationPreference: React.FC = () => {
  const navigate = useNavigate();
  const { data: settings, isLoading, error } = useNotificationSettings();
  const updateSettings = useUpdateNotificationSettings();

  return (
    <div className="np">
      <div className="np__header">
        <SBack onClick={() => navigate(-1)} />
        <span className="np__title">Notifications</span>
        <ThemeToggle />
      </div>

      {isLoading ? (
        <div className="np__loading">Loading…</div>
      ) : error || !settings ? (
        <div className="np__loading">Failed to load settings</div>
      ) : (
        <div className="np__body">
          <div>
            <p className="np__section-label">Preferences</p>
            <div className="np__card">
              <div className="np__row">
                <div className="np__row-text">
                  <span className="np__row-label">App Notifications</span>
                  <span className="np__row-sub">Push alerts for bookings and updates</span>
                </div>
                <Stoggle
                  isOn={settings.appNotification}
                  onToggle={v => updateSettings.mutate({ appNotification: v })}
                  disabled={updateSettings.isPending}
                  aria-label="Toggle app notifications"
                />
              </div>
              <div className="np__divider" />
              <div className="np__row">
                <div className="np__row-text">
                  <span className="np__row-label">Email Notifications</span>
                  <span className="np__row-sub">Booking confirmations and receipts</span>
                </div>
                <Stoggle
                  isOn={settings.emailNotification}
                  onToggle={v => updateSettings.mutate({ emailNotification: v })}
                  disabled={updateSettings.isPending}
                  aria-label="Toggle email notifications"
                />
              </div>
            </div>
          </div>

          <div>
            <p className="np__section-label">Push Categories</p>
            <div className="np__card">
              {PUSH_CHANNELS.map((channel, index) => (
                <React.Fragment key={channel.key}>
                  {index > 0 && <div className="np__divider" />}
                  <div className="np__row">
                    <div className="np__row-text">
                      <span className="np__row-label">{channel.label}</span>
                      <span className="np__row-sub">{channel.description}</span>
                    </div>
                    <Stoggle
                      isOn={settings.channels?.[channel.key] ?? true}
                      onToggle={v =>
                        updateSettings.mutate({ channels: { [channel.key]: v } })
                      }
                      /* The master switch already silences every category, so
                         leaving these live would let a member toggle something
                         that cannot take effect. */
                      disabled={updateSettings.isPending || !settings.appNotification}
                      aria-label={`Toggle ${channel.label.toLowerCase()}`}
                    />
                  </div>
                </React.Fragment>
              ))}
            </div>
            {!settings.appNotification && (
              <p className="np__section-note">
                Turn on App Notifications to choose categories.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPreference;
